// Who may see and do what, per property.
//
// Everything else in this portal is brokerage-wide: see one transaction and you
// see them all. Property management cannot work that way — a manager should see
// their own doors, an owner only their own buildings, and a tenant one unit. This
// is where that scope is granted.
//
// Two refusals the server enforces and this page explains up front, because being
// told after filling a form is worse than being told before:
//
//   * An owner or tenant grant must name an owner or a property. An unscoped one
//     would expose every client's figures to someone entitled to see one.
//   * A holder of an Illinois residential leasing agent licence may only be given
//     the residential sub-module, and only a leasing role. That licence permits
//     residential leasing activity and nothing else.
//
// Grants are revoked, never deleted. "Who could see what, and when" is the question
// asked after something goes wrong, and current state alone cannot answer it.

import React, { useState, useEffect, useCallback } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import {
    PageHeader, Panel, Tabs, Stat, Pill, Table, Modal, Input, Select,
    Button, Notice, Loading, money, fmtDate, titleCase,
} from '../../components/secure/pm/PMKit';

const ROLES = [
    'property_manager', 'bookkeeper', 'leasing_agent', 'maintenance_coordinator',
    'assistant', 'owner', 'tenant', 'vendor', 'readonly',
];

const ROLE_NOTE = {
    property_manager: 'Full operations on assigned properties. Cannot touch trust or sign agreements.',
    bookkeeper: 'Posts receipts and charges, reconciles, prepares statements. Cannot approve or release.',
    leasing_agent: 'Listings, showings, applications, lease preparation. No financial action.',
    maintenance_coordinator: 'Work orders, vendors, scheduling, inspections. No financial or lease action.',
    assistant: 'Clerical only — data entry, scheduling, document assembly.',
    owner: 'Their own properties: statements, occupancy, work orders. Must be scoped.',
    tenant: 'Their own lease only. Must be scoped.',
    vendor: 'Assigned work orders. No tenant or owner data beyond the job address.',
    readonly: 'Read, nothing more.',
};

export default function PMAuthorityPage() {
    const apiClient = useApiClient();
    const [tab, setTab] = useState('grants');
    const [loading, setLoading] = useState(true);
    const [grants, setGrants] = useState([]);
    const [audit, setAudit] = useState([]);
    const [properties, setProperties] = useState([]);
    const [owners, setOwners] = useState([]);
    const [portalAccounts, setPortalAccounts] = useState([]);
    const [tenants, setTenants] = useState([]);
    const [issued, setIssued] = useState(null);
    const [modal, setModal] = useState(null);
    const [form, setForm] = useState({});
    const [error, setError] = useState(null);
    const [busy, setBusy] = useState(false);

    const load = useCallback(async () => {
        try {
            const [g, a, p, o, pa, t] = await Promise.all([
                apiClient.get('/api/property-management/grants'),
                apiClient.get('/api/property-management/grants/audit'),
                apiClient.get('/api/property-management/properties'),
                apiClient.get('/api/property-management/owners'),
                apiClient.get('/api/property-management/portal-accounts'),
                apiClient.get('/api/property-management/tenants').catch(() => []),
            ]);
            setGrants(g || []); setAudit(a || []); setProperties(p || []); setOwners(o || []);
            setPortalAccounts(pa || []); setTenants(t || []);
        } catch (e) { setError({ message: e.message }); } finally { setLoading(false); }
    }, [apiClient]);

    useEffect(() => { load(); }, [load]);

    const act = async (fn) => {
        setBusy(true); setError(null);
        try { await fn(); setModal(null); setForm({}); await load(); }
        catch (e) { setError({ message: e.message }); }
        finally { setBusy(false); }
    };

    if (loading) return <Loading />;

    const live = grants.filter((g) => !g.revoked_at);
    const revoked = grants.filter((g) => g.revoked_at);

    return (
        <div>
            <PageHeader
                title="Property Management Authority"
                subtitle="The coarse role plus a grant. A manager sees their doors, an owner only their own buildings."
            >
                <Button variant="ghost" onClick={() => { setForm({ portal_role: 'owner' }); setIssued(null); setModal('portal'); }}>
                    Issue portal login
                </Button>
                <Button onClick={() => { setForm({ submodule: 'both', pm_role: 'property_manager' }); setModal('grant'); }}>
                    Grant authority
                </Button>
            </PageHeader>

            {error && <Notice tone="bad" message={error.message} onDismiss={() => setError(null)} />}

            <div className="grid grid-cols-3 gap-3 mb-6">
                <Stat label="Live grants" value={live.length} />
                <Stat label="Revoked" value={revoked.length} sub="kept, never deleted" />
                <Stat label="Audit entries" value={audit.length} />
            </div>

            <Tabs tabs={[
                { key: 'grants', label: 'Grants', count: live.length },
                { key: 'revoked', label: 'Revoked', count: revoked.length },
                { key: 'audit', label: 'Audit trail', count: audit.length },
                { key: 'portal', label: 'Portal logins', count: portalAccounts.filter((a) => a.is_active).length },
            ]} active={tab} onChange={setTab} />

            {tab === 'grants' && (
                <Panel>
                    <Table
                        columns={[
                            { key: 'username', label: 'User',
                                render: (r) => (
                                    <div>
                                        <div className="text-slate-800 font-medium">{r.username}</div>
                                        {r.license_type && r.license_type !== 'unknown' && (
                                            <div className="text-xs text-slate-400">
                                                {titleCase(r.license_type)} {r.license_number}
                                            </div>
                                        )}
                                    </div>
                                ) },
                            { key: 'pm_role', label: 'Role',
                                render: (r) => (
                                    <div>
                                        <Pill tone="indigo">{titleCase(r.pm_role)}</Pill>
                                        <div className="text-xs text-slate-400 mt-1 max-w-xs">{ROLE_NOTE[r.pm_role]}</div>
                                    </div>
                                ) },
                            { key: 'scope', label: 'Scope',
                                render: (r) => r.property_name
                                    ? <span className="text-slate-700">{r.property_name}</span>
                                    : r.owner_name
                                        ? <span className="text-slate-700">{r.owner_name}</span>
                                        : <Pill tone="warn">all properties</Pill> },
                            { key: 'submodule', label: 'Sub-module', nowrap: true,
                                render: (r) => <Pill tone={r.submodule === 'commercial' ? 'violet' : 'slate'}>
                                    {r.submodule}</Pill> },
                            { key: 'approval_limit', label: 'Approval limit', align: 'right',
                                render: (r) => r.approval_limit !== null ? money(r.approval_limit)
                                    : <span className="text-slate-300">none</span> },
                            { key: 'act', label: '', nowrap: true,
                                render: (r) => (
                                    <Button variant="ghost"
                                        onClick={() => act(() => apiClient.delete(`/api/property-management/grants/${r.grant_id}`))}>
                                        Revoke</Button>
                                ) },
                        ]}
                        rows={live.map((g) => ({ ...g, _key: g.grant_id }))}
                        empty="No grants. Only the managing broker and administrators can see the module."
                    />
                </Panel>
            )}

            {tab === 'revoked' && (
                <Panel subtitle="Kept so the question “who could see what, and when” stays answerable.">
                    <Table
                        columns={[
                            { key: 'username', label: 'User' },
                            { key: 'pm_role', label: 'Role', render: (r) => titleCase(r.pm_role) },
                            { key: 'scope', label: 'Scope',
                                render: (r) => r.property_name || r.owner_name || 'all properties' },
                            { key: 'granted_at', label: 'Granted', nowrap: true, render: (r) => fmtDate(r.granted_at) },
                            { key: 'revoked_at', label: 'Revoked', nowrap: true, render: (r) => fmtDate(r.revoked_at) },
                        ]}
                        rows={revoked.map((g) => ({ ...g, _key: g.grant_id }))}
                        empty="Nothing revoked."
                    />
                </Panel>
            )}

            {tab === 'audit' && (
                <Panel>
                    <Table
                        columns={[
                            { key: 'acted_at', label: 'When', nowrap: true, render: (r) => fmtDate(r.acted_at) },
                            { key: 'action', label: 'Action', nowrap: true,
                                render: (r) => <Pill tone={r.action === 'revoked' ? 'bad' : 'good'}>{r.action}</Pill> },
                            { key: 'username', label: 'Subject' },
                            { key: 'acted_by', label: 'By' },
                            { key: 'detail', label: 'Detail',
                                render: (r) => (
                                    <code className="text-xs text-slate-500">
                                        {typeof r.detail === 'string' ? r.detail : JSON.stringify(r.detail)}
                                    </code>
                                ) },
                        ]}
                        rows={audit.map((a) => ({ ...a, _key: a.audit_id }))}
                        empty="No changes recorded."
                    />
                </Panel>
            )}

            {tab === 'portal' && (
                <Panel
                    title="Owner and resident logins"
                    subtitle="Outside parties with a password into their own records. Deactivated, never deleted, and a withdrawal bites on their next request rather than at their next sign-in."
                >
                    <Table
                        columns={[
                            { key: 'party_name', label: 'Party',
                                render: (r) => (
                                    <div>
                                        <div className="text-slate-800 font-medium">{r.party_name || '—'}</div>
                                        <div className="text-xs text-slate-400">{r.login_email}</div>
                                    </div>
                                ) },
                            { key: 'portal_role', label: 'Role',
                                render: (r) => <Pill tone={r.portal_role === 'owner' ? 'indigo' : 'violet'}>{titleCase(r.portal_role)}</Pill> },
                            { key: 'is_active', label: 'Status',
                                render: (r) => (
                                    r.is_active
                                        ? <Pill tone={r.must_change_password ? 'warn' : 'good'}>
                                            {r.must_change_password ? 'Awaiting first sign-in' : 'Active'}
                                          </Pill>
                                        : <Pill tone="bad">Withdrawn</Pill>
                                ) },
                            { key: 'last_login_at', label: 'Last sign-in', nowrap: true,
                                render: (r) => fmtDate(r.last_login_at) },
                            { key: 'locked_until', label: 'Locked', nowrap: true,
                                render: (r) => (r.locked_until ? <Pill tone="bad">until {fmtDate(r.locked_until)}</Pill> : '—') },
                            { key: 'created_at', label: 'Issued', nowrap: true,
                                render: (r) => `${fmtDate(r.created_at)}${r.created_by ? ` by ${r.created_by}` : ''}` },
                            { key: 'actions', label: '', align: 'right',
                                render: (r) => (
                                    <div className="flex gap-2 justify-end">
                                        <Button variant="subtle"
                                            onClick={() => {
                                                setForm({
                                                    portal_role: r.portal_role,
                                                    party_id: r.owner_id || r.tenant_id,
                                                    login_email: r.login_email,
                                                });
                                                setIssued(null);
                                                setModal('portal');
                                            }}>
                                            Reset password
                                        </Button>
                                        {r.is_active ? (
                                            <Button variant="danger"
                                                onClick={() => act(() => apiClient.post(
                                                    `/api/property-management/portal-accounts/${r.portal_account_id}/deactivate`, {}))}>
                                                Withdraw
                                            </Button>
                                        ) : (
                                            <Button variant="ghost"
                                                onClick={() => act(() => apiClient.post(
                                                    `/api/property-management/portal-accounts/${r.portal_account_id}/reactivate`, {}))}>
                                                Restore
                                            </Button>
                                        )}
                                    </div>
                                ) },
                        ]}
                        rows={portalAccounts.map((a) => ({ ...a, _key: a.portal_account_id }))}
                        empty="No owner or resident has been given a login yet."
                    />
                </Panel>
            )}

            {modal === 'portal' && (
                <Modal
                    title={issued ? 'Portal login issued' : 'Issue a portal login'}
                    subtitle={issued ? null : 'The party reads their own records only. Nothing they see can be changed from the portal, and no money moves through it.'}
                    onClose={() => { setModal(null); setIssued(null); setForm({}); }}
                >
                    {issued ? (
                        <div>
                            <Notice tone="good" title="Read this password to them now" message={issued.message} />
                            <div className="bg-slate-100 border border-slate-300 rounded p-4 text-center">
                                <code className="text-xl font-bold tracking-wider text-slate-800">{issued.temporary_password}</code>
                            </div>
                            <p className="text-xs text-slate-500 mt-3">
                                It is stored only as a hash and cannot be shown again. They must replace it
                                before they can read anything. If it is lost, issue a reset rather than
                                searching for it.
                            </p>
                            <div className="flex justify-end mt-4">
                                <Button onClick={() => { setModal(null); setIssued(null); setForm({}); }}>Done</Button>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <Select label="Kind of party" value={form.portal_role || 'owner'}
                                onChange={(e) => setForm({ ...form, portal_role: e.target.value, party_id: '' })}
                                options={[{ value: 'owner', label: 'Property owner' }, { value: 'tenant', label: 'Resident / tenant' }]} />
                            {form.portal_role === 'tenant' ? (
                                <Select label="Resident" value={form.party_id || ''}
                                    onChange={(e) => {
                                        const t = tenants.find((x) => String(x.tenant_id) === e.target.value);
                                        setForm({ ...form, party_id: e.target.value, login_email: form.login_email || (t && t.email) || '' });
                                    }}
                                    options={[{ value: '', label: 'Choose a resident…' },
                                        ...tenants.map((t) => ({ value: t.tenant_id, label: t.legal_name }))]} />
                            ) : (
                                <Select label="Owner" value={form.party_id || ''}
                                    onChange={(e) => {
                                        const o = owners.find((x) => String(x.owner_id) === e.target.value);
                                        setForm({ ...form, party_id: e.target.value, login_email: form.login_email || (o && o.email) || '' });
                                    }}
                                    options={[{ value: '', label: 'Choose an owner…' },
                                        ...owners.map((o) => ({ value: o.owner_id, label: o.legal_name }))]} />
                            )}
                            <Input label="Login email" type="email" value={form.login_email || ''}
                                onChange={(e) => setForm({ ...form, login_email: e.target.value })}
                                hint="This is the username. It must not already belong to another portal account." />
                            <Notice tone="info"
                                message="Issuing a login to a party who already has one resets their password. It does not create a second account, because two live passwords to one book cannot be told apart afterwards." />
                            <div className="flex justify-end gap-2 mt-4">
                                <Button variant="ghost" onClick={() => { setModal(null); setForm({}); }}>Cancel</Button>
                                <Button disabled={busy || !form.party_id || !form.login_email}
                                    onClick={async () => {
                                        setBusy(true); setError(null);
                                        try {
                                            const r = await apiClient.post('/api/property-management/portal-accounts', form);
                                            setIssued(r);
                                            await load();
                                        } catch (e) { setError({ message: e.message }); }
                                        finally { setBusy(false); }
                                    }}>
                                    {busy ? 'Issuing…' : 'Issue login'}
                                </Button>
                            </div>
                        </div>
                    )}
                </Modal>
            )}

            {modal === 'grant' && (
                <Modal title="Grant property management authority"
                    subtitle="Granting is the managing broker's act — 1450.705 puts the supervision of every licensee on them by name."
                    onClose={() => setModal(null)} wide>
                    <div className="grid sm:grid-cols-2 gap-x-4">
                        <Input label="User ID" type="number" value={form.user_id || ''}
                            onChange={(e) => setForm({ ...form, user_id: e.target.value })}
                            hint="From Login Accounts." />
                        <Select label="Role" value={form.pm_role}
                            onChange={(e) => setForm({ ...form, pm_role: e.target.value })}
                            options={ROLES} hint={ROLE_NOTE[form.pm_role]} />
                        <Select label="Property scope" value={form.pm_property_id || ''}
                            onChange={(e) => setForm({ ...form, pm_property_id: e.target.value || null })}
                            options={[{ value: '', label: 'All properties the role permits' },
                                ...properties.map((p) => ({ value: p.pm_property_id, label: p.name }))]} />
                        <Select label="Client scope" value={form.owner_id || ''}
                            onChange={(e) => setForm({ ...form, owner_id: e.target.value || null })}
                            options={[{ value: '', label: 'None' },
                                ...owners.map((o) => ({ value: o.owner_id, label: o.legal_name }))]} />
                        <Select label="Sub-module" value={form.submodule}
                            onChange={(e) => setForm({ ...form, submodule: e.target.value })}
                            options={['both', 'residential', 'commercial']}
                            hint="A residential leasing agent licence permits residential only, and the grant is refused otherwise." />
                        <Input label="Work order approval limit" type="number" step="0.01"
                            value={form.approval_limit ?? ''}
                            onChange={(e) => setForm({ ...form, approval_limit: e.target.value })}
                            hint="Leave blank for none." />
                    </div>
                    {['owner', 'tenant'].includes(form.pm_role) && !form.owner_id && !form.pm_property_id && (
                        <Notice tone="warn"
                            message={`An unscoped ${form.pm_role} grant would expose every client's figures. Name a client or a property.`} />
                    )}
                    <div className="flex justify-end gap-2 mt-4">
                        <Button variant="ghost" onClick={() => setModal(null)}>Cancel</Button>
                        <Button disabled={busy}
                            onClick={() => act(() => apiClient.post('/api/property-management/grants', form))}>
                            {busy ? 'Granting…' : 'Grant'}</Button>
                    </div>
                </Modal>
            )}
        </div>
    );
}
